import { BulletBotPlusInfo } from "../types/Contract/HelperBot/BulletBotPlusInfo";
import { field, formatUnits } from "./viem";
import { HelperBotInfo } from "../types/Contract/HelperBot/HelperBotInfo";
import { HelperSmuggleBotInfo } from "../types/Contract/HelperBot/HelperSmuggleBotInfo";
import { UserBotPerkInfo } from "../types/Contract/HelperBot/UserBotPerkInfo";
import { XPMarketItemType } from "../types/XPMarketItem";

export const parseNewCarNickInfo = (data: any) => {
  return {
    criminal: data.criminal,
    crimeType: parseInt(data.crimeType),
    isSuccess: Boolean(data.isSuccess),
    isJailed: Boolean(data.isJailed),
    cashAmount: parseFloat(data.cashAmount),
    xpPoint: parseInt(data.xpPoint),
    nextNickTime: Number(data.nextNickTime),
    timestamp: Number(data.timestamp),
    block: Number(data.block),
    inventoryItemId: Number(data.inventoryItemId),
    cityId: Number(data.cityId),
    carType: Number(data.carType),
    damagePercent: Number(data.damagePercent),
    successNonce: Number(data.successNonce),
  };
};

/**
 * Parse NewCarNick event from contract transaction receipt (event.returnValues)
 */
export const parseNewCarNickEvent = (event: { returnValues: Record<string, unknown> }) => {
  const v = event.returnValues;
  return {
    criminal: String(v.criminal ?? ""),
    crimeType: Number(v.crimeType ?? 0),
    isSuccess: Boolean(v.isSuccess),
    isJailed: Boolean(v.isJailed),
    xpPoint: Number(v.xpPoint ?? 0),
    nextNickTime: Number(v.nextNickTime ?? 0),
    inventoryItemId: Number(v.inventoryItemId ?? 0),
    cityId: Number(v.cityId ?? 0),
    carType: Number(v.carType ?? 0),
    damagePercent: Number(v.damagePercent ?? 0),
    timestamp: Number(v.timestamp ?? 0),
    successNonce: Number(v.successNonce ?? 0),
  };
};

export const parseNewCrimeInfo = (data: any) => {
  return {
    criminal: data.criminal,
    crimeType: parseInt(data.crimeType),
    isSuccess: Boolean(data.isSuccess),
    isJailed: Boolean(data.isJailed),
    cashAmount: parseFloat(data.cashAmount),
    xpPoint: parseInt(data.xpPoint),
    nextCrimeTime: data.nextCrimeTime,
    timestamp: data.timestamp,
  };
};

/**
 * Parse NewCrime event from contract transaction receipt (event.returnValues)
 */
export const parseNewCrimeEvent = (event: { returnValues: Record<string, unknown> }) => {
  const v = event.returnValues;
  return {
    criminal: String(v.criminal ?? ""),
    crimeType: Number(v.crimeType ?? 0),
    isSuccess: Boolean(v.isSuccess),
    isJailed: Boolean(v.isJailed),
    cashAmount: Number(formatUnits(String(v.cashAmount ?? 0), 18)),
    xpPoint: Number(v.xpPoint ?? 0),
    nextCrimeTime: Number(v.nextCrimeTime ?? 0),
    timestamp: Number(v.timestamp ?? 0),
  };
};

export const parseBoozeBuyInfo = (data: any) => {
  return {
    buyer: data.buyer,
    isSuccess: Boolean(data.isSuccess),
    isJailed: Boolean(data.isJailed),
    totalUserBoozeCount: Number(data.totalUserBoozeCount),
    cashAmount: parseFloat(data.cashAmount),
    xpPoint: Number(data.xpPoint),
    nextBoozeTime: Number(data.nextBoozeTime),
    timestamp: Number(data.timestamp),
  };
};

export const parseBoozeSellInfo = (data: any) => {
  return {
    seller: data.seller,
    isSuccess: Boolean(data.isSuccess),
    isJailed: Boolean(data.isJailed),
    totalUserBoozeCount: Number(data.totalUserBoozeCount),
    cashAmount: parseFloat(data.cashAmount),
    owner: data.owner,
    ownerProfit: parseFloat(data.ownerProfit),
    xpPoint: Number(data.xpPoint),
    nextBoozeTime: Number(data.nextBoozeTime),
    timestamp: Number(data.timestamp),
  };
};

export const parseNarcsBuyInfo = (data: any) => {
  return {
    buyer: data.buyer,
    isSuccess: Boolean(data.isSuccess),
    isJailed: Boolean(data.isJailed),
    totalUserNarcsCount: Number(data.totalUserNarcsCount),
    cashAmount: parseFloat(data.cashAmount),
    xpPoint: Number(data.xpPoint),
    nextNarcsTime: Number(data.nextNarcsTime),
    timestamp: Number(data.timestamp),
  };
};

export const parseNarcsSellInfo = (data: any) => {
  return {
    seller: data.seller,
    isSuccess: Boolean(data.isSuccess),
    isJailed: Boolean(data.isJailed),
    totalUserNarcsCount: Number(data.totalUserNarcsCount),
    cashAmount: parseFloat(data.cashAmount),
    owner: data.owner,
    ownerProfit: parseFloat(data.ownerProfit),
    xpPoint: Number(data.xpPoint),
    nextNarcsTime: Number(data.nextNarcsTime),
    timestamp: Number(data.timestamp),
  };
};

export const parseNewKillSkillInfo = (data: any) => {
  return {
    trainer: data.trainer,
    trainType: Number(data.trainType),
    isSuccess: Boolean(data.isSuccess),
    trainCost: parseFloat(data.trainCost),
    xpPoint: Number(data.xpPoint),
    nextTrainTime: Number(data.nextTrainTime),
    timestamp: Number(data.timestamp),
  };
};

/**
 * Parse TrainedSkill event from contract transaction receipt (event.returnValues)
 */
export const parseTrainedSkillEvent = (event: { returnValues: Record<string, unknown> }) => {
  const v = event.returnValues;
  return {
    trainer: String(v.trainer ?? ""),
    trainType: Number(v.trainType ?? 0),
    isSuccess: Boolean(v.isSuccess),
    trainCost: Number(v.trainCost ?? 0),
    xpPoint: Number(v.xpPoint ?? 0),
    nextTrainTime: Number(v.nextTrainTime ?? 0),
    timestamp: Number(v.timestamp ?? 0),
  };
};

export const parseTrainSkillRequestedEvent = (event: {
  returnValues: Record<string, unknown>;
}) => {
  const v = event.returnValues;
  return {
    trainer: String(v.trainer ?? ""),
    trainType: Number(v.trainType ?? 0),
    requestBlock: Number(v.requestBlock ?? 0),
    trainCost: Number(v.trainCost ?? 0),
    timestamp: Number(v.timestamp ?? 0),
  };
};

export const parseHelperBotInfo = (data: any) => {
  return {
    successRate: Number(field(data, "successRate", 0)),
    startTimestamp: Number(field(data, "startTimestamp", 1)),
    endTimestamp: Number(field(data, "endTimestamp", 2)),
    attemptCount: Number(field(data, "attemptCount", 3)),
    isRunning: Boolean(field(data, "isRunning", 4)),
  } as HelperBotInfo;
};

export const parseUserBotPerkInfo = (data: any) => {
  return {
    successRatePerk: Number(field(data, "successRatePerk", 0)),
    cooldownReductionPerk: Number(field(data, "cooldownReductionPerk", 1)),
    cashRewardPerk: Number(field(data, "cashRewardPerk", 2)),
    creditCostReductionPerk: Number(field(data, "creditCostReductionPerk", 3)),
    bulletDoublePerk: Number(field(data, "bulletDoublePerk", 4)) === 1,
    perkDuration: Number(field(data, "perkDuration", 5)),
  } as UserBotPerkInfo;
};

export const parseBulletBotPlusInfo = (data: any) => {
  return {
    amountModifiedPercent: Number(field(data, "amountModifiedPercent", 0)),
    priceModifiedPercent: Number(field(data, "priceModifiedPercent", 1)),
  } as BulletBotPlusInfo;
};

export const parseHelperSmuggleBotInfo = (data: any) => {
  return {
    successRate: Number(field(data, "successRate", 0)),
    startTimestamp: Number(field(data, "startTimestamp", 1)),
    endTimestamp: Number(field(data, "endTimestamp", 2)),
    attemptCount: Number(field(data, "attemptCount", 3)),
    amountPerPurchase: Number(field(data, "amountPerPurchase", 4)),
    isRunning: Boolean(field(data, "isRunning", 5)),
  } as HelperSmuggleBotInfo;
};

export const parseHelperBotEndedInfo = (data: any) => {
  return {
    user: data.user,
    botType: Number(data.botType),
    xpEarned: Number(data.xpEarned),
    cashEarned: parseFloat(data.cashEarned),
    carCount: Number(data.carCount),
    cashSpent: Number(data.cashSpent),
    bulletEarned: Number(data.bulletEarned),
    ownerProfit: parseFloat(data.ownerProfit),
    timestamp: Number(data.timestamp),
  };
};

export const parseBulletBuyInfo = (data: any) => {
  return {
    buyer: data.buyer,
    isSuccess: Boolean(data.isSuccess),
    cashAmount: parseFloat(data.cashAmount),
    bulletAmount: Number(data.bulletAmount),
    nextBuyTime: Number(data.nextBuyTime),
    timestamp: Number(data.timestamp),
  };
};

export const parseNewXPMarketInfo = (data: any) => {
  const res: XPMarketItemType = {
    id: Number(data.id),
    seller: data.seller,
    buyer: data.buyer,
    xpType: Number(data.xpType),
    xpPoint: Number(data.xpPoint),
    token: data.token,
    startingPrice: String(data.startingPrice),
    currentPrice: String(data.currentPrice),
    status: Number(data.status),
    listingType: Number(data.listingType),
    expireAt: Number(data.expiresAt),
    timestamp: Number(data.timestamp),
    txHash: data.txHash,
  };

  return res;
};

export const parseNewBidInfo = (data: XPMarketItemType, newData: any) => {
  return {
    ...data,
    buyer: newData.buyer,
    currentPrice: String(newData.price),
    timestamp: Number(newData.timestamp),
    expireAt: Number(newData.endTimestamp),
    txHash: newData.txHash,
  } as XPMarketItemType;
};

export const parseHealthBuyInfo = (data: any) => {
  return {
    buyer: data.buyer,
    isSuccess: Boolean(data.isSuccess),
    cashAmount: parseFloat(data.cashAmount),
    healthAmount: Number(data.healthAmount),
    nextBuyTime: Number(data.nextBuyTime),
    timestamp: Number(data.timestamp),
  };
};

export const parseMysteryBoxClaimedInfo = (data: any) => {
  return {
    user: data.user,
    itemId: Number(data.itemId),
    mysteryBoxIndex: Number(data.mysteryBoxIndex),
    claimedAt: Number(data.claimedAt),
    txHash: data.txHash,
  };
};
